import { useEffect, useRef, useState } from 'react';
import CustomInput from 'components/common/CustomInput';
import CustomButton from 'components/common/CustomButton';
import { toast } from 'react-toastify';
import CustomSelect from 'components/common/CustomSelect';
import { layer } from 'data/layer';
import AuthButton from 'components/common/AuthButton';
import axios from 'axios';
import { formAxios, jsonAxios } from 'components/axios';
import { ICompanyInfo } from 'types/user';
import { formatDate } from 'components/function';
import DatePicker from 'react-datepicker';
import { ko } from "date-fns/locale";
import MyPageTemplate from 'components/template/MyPageTemplate';
import { ReactComponent as FileIcon } from 'assets/common/file.svg'
import DragFile from 'components/common/DragFile';
import ChangeCompanyCode from 'components/common/modal/ChangeCompanyCode';
import { useGetUser } from 'atom/user';

const EditCompany = () => {
    interface ICompanyMenu {
        title: string,
        key: string,
        name: string,
        type: string,
        placeholder: string,
        value: string | undefined
        disabled?: boolean
        show?: boolean
        require?: boolean
    }
    const user = useGetUser();
    const [companyInfo, setCompanyInfo] = useState<ICompanyInfo>({} as ICompanyInfo);
    const [info, setInfo] = useState<ICompanyInfo>({} as ICompanyInfo);
    const [file, setFile] = useState<File | null>(null);
    const [popOpen, setPopOpen] = useState<boolean>(false)
    const [clear, setClear] = useState<boolean>(false)
    const [isAuth, setIsAuth] = useState<boolean>(false)
    const companyMenu: ICompanyMenu[] = [
        {
            title: '기업명(한글)',
            key: 'companyNameKr',
            name: 'companyNameKr',
            type: 'text',
            placeholder: '예) 사업자 등록증에 기재된 기업명을 적어주세요',
            value: companyInfo.companyNameKr,
            require: true,
            disabled: true
        },
        {
            title: '기업명(영문)',
            key: 'companyNameEn',
            name: 'companyNameEn',
            type: 'text',
            placeholder: '예) Theinsight, LtD',
            value: companyInfo.companyNameEn,
            require: false,
            disabled: true
        },
        {
            title: '사업구분',
            key: 'layer',
            name: 'layer',
            type: 'text',
            placeholder: '사업구분을 선택해주세요',
            value: companyInfo.layer,
            require: true,
            disabled: true
        },
        {
            title: '사업자 등록 번호',
            key: 'bizNumberUser',
            name: 'bizNumberUser',
            type: 'text',
            placeholder: '-를 포함하지 않은 숫자만 입력해주세요',
            value: companyInfo.bizNumberUser,
            require: true,
            disabled: true
        },
        {
            title: '대표자명',
            key: 'ceoName',
            name: 'ceoName',
            type: 'text',
            placeholder: '대표자 실명',
            value: companyInfo.ceoName,
            require: true,
            disabled: true
        },
        {
            title: '개업년월일',
            key: 'establishedAt',
            name: 'establishedAt',
            type: 'date',
            placeholder: '개업년월일',
            value: companyInfo.establishedAt,
            require: true,
            disabled: true
        },
        {
            title: '연락처',
            key: 'phoneNumber',
            name: 'phoneNumber',
            type: 'text',
            placeholder: '연락 가능 번호',
            value: companyInfo.phoneNumber,
            require: true,
            disabled: true
        }
    ]
    const [startDate, setStartDate] = useState<Date | null>(null);
    const isInitRef = useRef<boolean>(true);

    useEffect(() => {
        if (isInitRef.current) {
            isInitRef.current = false;
            return;
        }
        setCompanyInfo(p => ({
            ...p,
            establishedAt: startDate ? formatDate(startDate) : undefined
        }));
    }, [startDate]);

    useEffect(() => {
        init();
    }, [])

    useEffect(() => {
        if (JSON.stringify(companyInfo) === JSON.stringify(info)) {
            setClear(false)
        }
        else {
            setClear(true)
        }

        const infoField: (keyof ICompanyInfo)[] = [
            'companyNameKr',
            'ceoName',
            'establishedAt',
            'bizNumberUser'
        ]

        const hasChangedField = infoField.some(
            key => companyInfo[key] !== info[key]
        )

        if (hasChangedField) {
            setIsAuth(false)
        }

    }, [companyInfo])

    const init = () => {
        jsonAxios.get('user/info').then(res => {
            console.log(res.data)
            if (res.data.code === 200) {
                setCompanyInfo(res.data.data.companyInfo)
                setInfo(res.data.data.companyInfo)
                const establishedAt = res.data.data.companyInfo.establishedAt
                setStartDate(establishedAt ? new Date(establishedAt) : null)
                setIsAuth(true)
            }
        })
    }

    const saveChange = () => {
        for (const field of requiredFields) {
            const value = companyInfo[field.key];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                toast.error(`${field.label} 입력해 주세요.`);
                return;
            }
        }

        if (!isAuth) {
            return toast.error(`사업자 등록 번호 인증을 해주세요.`);
        }

        const reqData = {
            companyId: companyInfo.companyId,
            companyNameKr: companyInfo.companyNameKr,
            companyNameEn: companyInfo.companyNameEn,
            ceoName: companyInfo.ceoName,
            layer: companyInfo.layer,
            phone: companyInfo.phoneNumber,
            bizNumber: companyInfo.bizNumberUser,
            establishedAt: companyInfo.establishedAt
        }

        jsonAxios.post('company/update', reqData).then(res => {
            console.log(res)
            if (res.data.code === 200) {
                toast.success('정상적으로 수정되었습니다.')
                init();
            }
        })
    }

    const requiredFields: { key: keyof typeof companyInfo, label: string }[] = [
        { key: 'bizNumberUser', label: '사업자 등록번호' },
        { key: 'establishedAt', label: '개업년월일' },
        { key: 'ceoName', label: '대표자명' },
        { key: 'companyNameKr', label: '기업명(한글)' },
        { key: 'phoneNumber', label: '연락처' },
    ];

    const checkBusinessCode = async () => {
        for (const field of requiredFields) {
            const value = companyInfo[field.key];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                toast.error(`${field.label} 입력해 주세요.`);
                return;
            }
        }

        const reqData =
        {
            businesses: [{
                "b_no": companyInfo.bizNumberUser.replaceAll('-', ''), // 사업자 등록번호, 법인 등록번호
                "start_dt": companyInfo.establishedAt?.replaceAll('-', ''), // 사업 시작일
                "p_nm": companyInfo.ceoName, // 대표자명
                "b_nm": companyInfo.companyNameKr // 회사 이름
            }]
        }

        try {
            await axios.post(`https://api.odcloud.kr/api/nts-businessman/v1/validate?serviceKey=${process.env.REACT_APP_OPEN_API_KEY}`, reqData).then(res => {
                console.log(res.data.data[0])
                if (res.data.data[0].valid === '01') {
                    toast.success('정상적으로 확인되었습니다.');
                    setCompanyInfo(p => ({
                        ...p,
                        companyVaild: true
                    }))
                    setIsAuth(true)
                }
                else {
                    toast.error('존재하지 않는 정보입니다.');
                    setCompanyInfo(p => ({
                        ...p,
                        companyVaild: false
                    }))
                    setIsAuth(false)
                }
            })
        } catch (err: any) {
            console.log(err)

            if (err.response.data.code === -5) {
                toast.error(`현재 api 문제로 인증이 불가합니다. 수정은 가능합니다. 수정버튼을 눌러주세요.`);
                // toast.error(`${err.response.data.msg}`);
                setIsAuth(true)
            }
        }
    }

    const showPDF = (url: string) => {
        url !== '' && window.open(url, '_blank');
    }

    const uploadFile = () => {
        if (file) {
            const formData = new FormData();
            formData.append('file', file)
            console.log(file);
            formAxios.post(`company/${companyInfo.companyId}/file`, formData).then(res => {
                console.log(res.data)
                if (res.data.code === 200) {
                    toast.success('정상적으로 업로드 되었습니다.')
                    init();
                }
            })
        }
    }

    return (
        <MyPageTemplate background='bg-[#F8F8FA]'>
            <div className='flex flex-col gap-25 h-full min-h-dvh'>
                <div className="flex flex-col gap-6 pt-10 tablet:pt-4 tablet:pb-4">
                    <div
                        className='bg-white hover:shadow-lg p-10 mobile:p-6 rounded-xl transition-all'
                    >
                        <div className='mobile:flex mobile:flex-col gap-x-10 gap-y-6 mobile:gap-3 grid grid-rows-[repeat(3,auto)_1fr] grid-flow-col auto-cols-[48%] mb-12 mobile:mb-6 w-full max-w-[1100px]'>
                            {
                                companyMenu.map((item, idx) => (
                                    <div className='flex flex-col items-start gap-3 shrink-0' key={idx}>
                                        <p className='mobile:flex mobile:flex-col font-semibold mobile:text-base text-2xl'>
                                            <span>
                                                {item.title}
                                                {
                                                    user.isMaster &&
                                                    item.require &&
                                                    <span className='text-red'> *</span>
                                                }
                                            </span>
                                            {
                                                item.key === 'bizNumberUser' &&
                                                <span className='text-red text-sm'>사업자 등록 번호당 한아이디만 가입가능합니다.</span>
                                            }
                                            {
                                                item.key === 'establishedAt' &&
                                                <span className='text-red text-sm'>ex) 2025-01-01</span>
                                            }
                                        </p>
                                        {
                                            item.key === 'establishedAt' ?
                                                <div className='w-full'>
                                                    <DatePicker
                                                        className={`bg-[#F8F8FA] px-6 rounded-lg border border-transparent hover:border-[#B3B3B3] h-15 w-full text-lg tablet:h-10 tablet:text-sm tablet:px-4`}
                                                        onChange={(value: Date | null | [Date | null, Date | null]) => {
                                                            if (value instanceof Date && value !== null) {
                                                                setStartDate(value);
                                                            }
                                                            else if (value === null) {
                                                                setStartDate(null)
                                                            }
                                                        }}
                                                        maxDate={new Date()}
                                                        disabled={!user.isMaster}
                                                        selected={startDate}
                                                        calendarClassName="rounded-lg shadow-lg border"
                                                        locale={ko}
                                                        dateFormat="yyyy-MM-dd"
                                                        placeholderText={"YYYY-MM-DD"}
                                                    />
                                                </div>
                                                :
                                                item.key === 'layer' ?
                                                    <CustomSelect
                                                        className={'w-full'}
                                                        option={layer}
                                                        isWhite={false}
                                                        disabled={!user.isMaster}
                                                        value={
                                                            companyInfo.layer !== undefined
                                                                ? layer.find(arr => arr.value === companyInfo.layer)
                                                                : undefined
                                                        }
                                                        placeholder={item.placeholder}
                                                        onChange={(value) => {
                                                            setCompanyInfo({
                                                                ...companyInfo,
                                                                layer: value
                                                            })
                                                        }}
                                                        border={false}
                                                    />

                                                    :
                                                    item.key === 'bizNumberUser' ?
                                                        <div className='flex gap-3 w-full'>
                                                            <CustomInput
                                                                type={item.type}
                                                                value={item.value ?? ""}
                                                                name={item.name}
                                                                disabled={companyInfo.companyVaild}
                                                                readonly={!user.isMaster}
                                                                placeholder={item.placeholder}
                                                                onChange={(e) => {
                                                                    setCompanyInfo({
                                                                        ...companyInfo,
                                                                        [item.key]: e.target.value
                                                                    })
                                                                }}
                                                            />
                                                            {
                                                                user.isMaster &&
                                                                <AuthButton
                                                                    title='인증하기'
                                                                    disabled={companyInfo.companyVaild}
                                                                    className={`shrink-0 ${(companyInfo.bizNumberUser?.length > 0) ? 'text-primary border-primary' : 'text-[#9D9EB7] border-[#9D9EB7]'}`}
                                                                    onClick={checkBusinessCode}
                                                                />
                                                            }
                                                        </div>

                                                        :
                                                        <CustomInput
                                                            type={item.type}
                                                            value={item.value ?? ""}
                                                            name={item.name}
                                                            disabled={!['phoneNumber', 'companyNameEn'].includes(item.key) && companyInfo.companyVaild}
                                                            readonly={!user.isMaster ? item.key !== 'phoneNumber' : false}
                                                            placeholder={item.placeholder}
                                                            onChange={(e) => {
                                                                setCompanyInfo({
                                                                    ...companyInfo,
                                                                    [item.key]: e.target.value
                                                                })
                                                            }}
                                                        />
                                        }
                                    </div>
                                ))
                            }
                            <div className='w-full'>
                                <div className='flex flex-col justify-between mb-6 w-full'>
                                    <p className='mb-3 font-semibold mobile:text-base text-2xl'>
                                        사업자 등록증 {user.isMaster && <span className='text-red'> *</span>}
                                    </p>
                                    <div className='mb-3'>
                                        {/* 사업자 있으면 그냥 없으면 파일 노출 */}
                                        {
                                            companyInfo?.companyFileInfo?.fileName ?
                                                <div className={`flex flex-col items-center justify-center border-2 border-dashed gap-3 h-35 rounded cursor-pointer
                                            bg-[#F8F8FA] text-xs
                                            `}>
                                                    <div
                                                        className="relative flex flex-col justify-center items-center gap-2 w-full h-full"
                                                        onClick={() => showPDF(companyInfo.companyFileInfo.filePath || '')}
                                                    >
                                                        <FileIcon />
                                                        <p className="text-black">{companyInfo?.companyFileInfo?.fileName}</p>
                                                    </div>
                                                </div>
                                                :
                                                <DragFile
                                                    description='선택 또는 드래그하여<br />사업자 등록증 파일을 등록해 주세요'
                                                    onChangeFile={(file) => {
                                                        setFile(file)
                                                    }}
                                                />
                                        }
                                    </div>
                                    {
                                        user.isMaster &&
                                        <>
                                            <p className='text-[#A8A9BF] text-xs'>*견적 진행 및 운송정보 확인을 위해 반드시 사업자 등록증을 첨부하여 주시기 바랍니다.</p>
                                            <p className='text-[#A8A9BF] text-xs'>*파일은 PDF형식의 15MB로 제한됩니다.</p>
                                        </>
                                    }
                                </div>

                                {
                                    user.isMaster &&
                                    (
                                        companyInfo?.companyFileInfo?.fileName ?
                                            <CustomButton
                                                className='!border-primary w-full !text-primary hover:!text-white'
                                                title='사업자 등록증 변경'
                                                onClick={() => setPopOpen(true)}
                                            /> :
                                            <CustomButton
                                                className='!border-primary w-full !text-primary hover:!text-white'
                                                title='사업자 등록증 저장'
                                                onClick={() => uploadFile()}
                                            />
                                    )
                                }
                            </div>
                        </div>
                        <CustomButton
                            disabled={!clear}
                            className='bg-primary w-[200px]'
                            title='수정'
                            onClick={saveChange}
                        />
                    </div>
                </div>
            </div>
            <ChangeCompanyCode
                isOpen={popOpen}
                setIsOpen={setPopOpen}
                companyId={companyInfo.companyId}
                getUser={init}
            />
        </MyPageTemplate>
    )
}

export default EditCompany